import { ExportOutlined } from '@ant-design/icons';
import { useFlowContext } from '_/contexts/FlowContext';
import { useToggleFlowDestinationsOverride } from '_/hooks/useFlows';
import useToggleValue from '_/hooks/useToggleValue';
import { Switch, Button, Modal } from 'antd';
import Tooltip from 'antd/lib/tooltip';
import React, { ReactElement, SyntheticEvent, useCallback } from 'react';

import { IFlowDestinationsPanelHeaderProps } from './FlowDestinations.types';

const FlowDestinationsPanelHeader = ({
  flowSegmentId,
  header,
  href,
  overrideDestinations,
}: IFlowDestinationsPanelHeaderProps): ReactElement => {
  const [open, toggleOpen] = useToggleValue();
  const { flowId } = useFlowContext();
  const { mutate: toggleOverride } = useToggleFlowDestinationsOverride(flowId);

  const title = overrideDestinations ? 'Reset to Default Flow Destinations' : 'Create Custom Flow Destinations';

  const unlinkTooltip = `Click to ${overrideDestinations ? 'reset to default' : 'create custom flow'} destinations list`;

  const description = overrideDestinations
    ? `Are you sure that you want to delete all custom destinations for "${header}"?`
    : `Are you sure that you want to create the "${header}" custom destinations?`;

  const onOpenCollectionDestinationsClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      const collectionDestinationsPage = `${window.location.origin}${href}`;
      window.open(collectionDestinationsPage, '_blank');
    },
    [href],
  );

  const onSwitchChange = useCallback(
    (_: boolean, e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      toggleOpen();
    },
    [toggleOpen],
  );

  const handleConfirm = useCallback(() => {
    toggleOverride({
      flowSegmentId: flowSegmentId,
      overrideDestinations: !overrideDestinations,
    });
    toggleOpen();
  }, [toggleOverride, flowSegmentId, overrideDestinations, toggleOpen]);

  const onModalClick = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>{header}</div>
        <div style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
          <div style={{ marginRight: '20px', display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Open collection destinations">
              <Button icon={<ExportOutlined />} onClick={onOpenCollectionDestinationsClick}>
                Open
              </Button>
            </Tooltip>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
            <div style={{ marginRight: '5px' }}>Default</div>
            <Tooltip title={unlinkTooltip}>
              <Switch checked={overrideDestinations} onChange={onSwitchChange} />
            </Tooltip>
            <div style={{ marginLeft: '5px' }}>Custom</div>
          </div>
        </div>
      </div>
      <div onClick={onModalClick}>
        <Modal open={open} title={title} onCancel={toggleOpen} onOk={handleConfirm}>
          <p>{description}</p>
        </Modal>
      </div>
    </>
  );
};

export default FlowDestinationsPanelHeader;
